import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge, RelationPicker } from './ui';
import { unwrapLookupItems } from './c360';
import { RELATION_CONFIGS } from './relationConfigs';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldCustom,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';
import { useFullRecordRefill } from './detail/useFullRecordRefill';
// #768 İl/İlçe combobox (trGeo) + telefon formatı (CustomerEditPage deseni).
import { getProvinces, getDistricts, toOptions } from '../utils/trGeo';
import { formatTrPhoneInput, isCompleteTrPhone } from '../utils/phone';
import { digitsOnly, isExactDigits } from '../utils/format';

const API_BASE = '/api/vendors';

// #768 Vendor'da Ülke alanı yok → İl/İlçe kaskadı Türkiye baz alınarak çözülür.
const GEO_COUNTRY = 'Türkiye';

// #768 Para birimi seçenekleri (CustomerEditPage CURRENCY_OPTIONS deseni).
const CURRENCY_OPTIONS = [
  { value: 'TL', label: '₺ TL — Türk Lirası' },
  { value: 'USD', label: '$ USD — Amerikan Doları' },
  { value: 'EUR', label: '€ EUR — Euro' },
  { value: 'GBP', label: '£ GBP — İngiliz Sterlini' },
  { value: 'CHF', label: '₣ CHF — İsviçre Frangı' },
];
// #768 Mevcut (listede olmayan) para birimi değerini koruyan opsiyon listesi üretir.
function currencyOptions(current) {
  const opts = [{ value: '', label: 'Seçiniz' }, ...CURRENCY_OPTIONS];
  if (current && !CURRENCY_OPTIONS.some((o) => o.value === current)) {
    opts.push({ value: current, label: `${current} (tanımsız)` });
  }
  return opts;
}

// status: 1 = Aktif, 0 = Pasif, 2 = Beklemede (VendorsPage STATUS_META ile aynı)
const STATUS_META = {
  1: { label: 'Aktif', tone: 'ok' },
  0: { label: 'Pasif', tone: 'neutral' },
  2: { label: 'Beklemede', tone: 'wait' },
};
// #768 Dropdown'da "Beklemede" (value 2) GÖSTERİLMEZ — sadece Aktif/Pasif seçilebilir.
// STATUS_META'da meta kalır (eski Beklemede kayıtlarının rozet etiketi için).
const STATUS_OPTIONS = [
  { value: '', label: 'Seçiniz' },
  ...Object.entries(STATUS_META)
    .filter(([value]) => Number(value) !== 2)
    .map(([value, meta]) => ({ value: Number(value), label: meta.label })),
];

const EMPTY_FORM = {
  textId: '', name: '', city: '', erpId: '', groupId: '', subGroupId: '',
  region1: '', region2: '', status: 1, vendorCurrency: '',
  // #768 Yeni alanlar (backend DTO camelCase ile kabul ediyor)
  person: '', identityNumber: '', address: '', district: '',
  postCode: '', phone: '', taxOffice: '', taxNumber: '',
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }
function toInt(v) { const n = parseInt(v, 10); return Number.isNaN(n) ? null : n; }

async function readApiError(res, fallback) {
  try {
    const data = await res.json();
    return data?.message || data?.Message || data?.infoMessage || data?.InfoMessage || fallback;
  } catch {
    return fallback;
  }
}

// Bir Vendor kaydını (GetById/list/satır state) ortak forma indirger.
function rowToForm(r) {
  return {
    textId: pick(r, 'textId', 'TextId') ?? '',
    name: pick(r, 'name', 'Name') ?? '',
    city: pick(r, 'city', 'City') ?? '',
    erpId: pick(r, 'erpId', 'ErpId') ?? '',
    groupId: pick(r, 'groupId', 'GroupId') ?? '',
    subGroupId: pick(r, 'subGroupId', 'SubGroupId') ?? '',
    region1: pick(r, 'region1', 'Region1') ?? '',
    region2: pick(r, 'region2', 'Region2') ?? '',
    status: pick(r, 'status', 'Status') ?? '',
    vendorCurrency: pick(r, 'vendorCurrency', 'VendorCurrency') ?? '',
    // #768 Yeni alanlar (OData satırından camelCase/PascalCase okunur)
    person: pick(r, 'person', 'Person') ?? '',
    identityNumber: pick(r, 'identityNumber', 'IdentityNumber') ?? '',
    address: pick(r, 'address', 'Address') ?? '',
    district: pick(r, 'district', 'District') ?? '',
    postCode: pick(r, 'postCode', 'PostCode') ?? '',
    phone: pick(r, 'phone', 'Phone') ?? '',
    taxOffice: pick(r, 'taxOffice', 'TaxOffice') ?? '',
    taxNumber: pick(r, 'taxNumber', 'TaxNumber') ?? '',
  };
}

export function VendorDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Distribütör' : 'Distribütör Detayı', 'Distribütör kartı bilgileri.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  const [groups, setGroups] = useState([]);
  const [subGroups, setSubGroups] = useState([]);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  // #768.4 Kullanıcı forma dokunduysa arka plandaki tam-kayıt tazelemesi onu ezmesin.
  const { markDirty, refillIfClean } = useFullRecordRefill();
  const set = (k, v) => { markDirty(); setForm((c) => ({ ...c, [k]: v })); };

  const getGroupLabel = useCallback((gid) => {
    const it = groups.find((item) => item.id === gid);
    return it?.description || it?.textId || '';
  }, [groups]);
  const getSubGroupLabel = useCallback((gid) => {
    const it = subGroups.find((item) => item.id === gid);
    return it?.description || it?.textId || '';
  }, [subGroups]);

  // Lookup'lar: grup / alt grup (RelationPicker etiketleri için)
  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/vendorgroups/Select', { headers }).then((res) => (res.ok ? res.json() : [])),
      fetch('/api/vendorsubgroups/Select', { headers }).then((res) => (res.ok ? res.json() : [])),
    ]).then(([groupData, subGroupData]) => {
      setGroups(unwrapLookupItems(groupData));
      setSubGroups(unwrapLookupItems(subGroupData));
    }).catch(() => {});
  }, []);

  // Tek kayıt yükleme: önce location.state.row; yoksa GetById; yoksa listeden.
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    if (location.state?.row) {
      const sr = location.state.row;
      setForm(rowToForm(sr));
      // #768.4 Nav-state satırı liste OData $select'inden gelir → yalnız GÖRÜNÜR kolonları
      // içerir. İlgili Kişi / Adres / TC / Vergi No / Posta Kodu / Bölge 1-2 / Para Birimi
      // gibi alanlar projeksiyonda olmadığı için formda BOŞ görünüyordu (kayıt DB'de dolu
      // olduğu hâlde). Tam kaydı arka planda çekip formu ve audit'i ondan tazeliyoruz.
      (async () => {
        try {
          let rec = null;
          const res = await fetch(`${API_BASE}/GetById?Id=${encodeURIComponent(id)}`, { headers: authHeaders() });
          if (res.ok) {
            const r = unwrap(await res.json());
            if (r && pick(r, 'id', 'Id')) rec = r;
          }
          if (!rec) {
            const listRes = await fetch(`${API_BASE}/Select`, { headers: authHeaders() });
            if (listRes.ok) {
              const items = unwrap(await listRes.json());
              rec = (Array.isArray(items) ? items : []).find((x) => String(pick(x, 'id', 'Id')) === String(id));
            }
          }
          if (rec) {
            refillIfClean(() => setForm(rowToForm(rec)));
            setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
          }
        } catch { /* tam kayıt best-effort; kısmi satır ekranda kalır */ }
      })();
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/GetById?Id=${encodeURIComponent(id)}`, { headers: authHeaders() });
      if (res.ok) {
        const r = unwrap(await res.json());
        if (r && (pick(r, 'id', 'Id'))) {
          setForm(rowToForm(r));
          setAudit({ createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(r, 'createdDate', 'CreatedDate'), updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
          setLoading(false);
          return;
        }
      }
      // GetById boş döndüyse listeden bul
      const listRes = await fetch(`${API_BASE}/Select`, { headers: authHeaders() });
      if (!listRes.ok) throw new Error(`Distribütör alınamadı (${listRes.status})`);
      const items = unwrap(await listRes.json());
      const found = (Array.isArray(items) ? items : []).find((x) => String(pick(x, 'id', 'Id')) === String(id));
      if (!found) throw new Error('Distribütör bulunamadı');
      setForm(rowToForm(found));
      setAudit({ createdBy: pick(found, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(found, 'createdDate', 'CreatedDate'), updatedBy: pick(found, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(found, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    } catch (err) {
      showToast('error', err.message || 'Distribütör alınamadı');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, location.state, refillIfClean]);

  useEffect(() => { load(); }, [load]);

  // Klonla: /vendors/new'e kopya veriyle gelindiyse formu doldur (kayıt OLUŞTURMA — kullanıcı Kaydet diyene kadar).
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // VendorsPage.handleSave payload'u AYNEN korundu (customerRelationId + vendorCurrencyTypeDeleted... null).
  const buildPayload = () => ({
    textId: form.textId.trim(),
    name: form.name.trim(),
    city: form.city,
    erpId: form.erpId,
    groupId: form.groupId || null,
    subGroupId: form.subGroupId || null,
    region1: form.region1,
    region2: form.region2,
    status: toInt(form.status),
    vendorCurrency: form.vendorCurrency,
    // #768 Yeni alanlar → backend DTO (CreateVendorRequest/VendorWriteRequest) AutoMapper ile Vendor entity'sine map eder.
    person: form.person,
    identityNumber: form.identityNumber,
    address: form.address,
    district: form.district,
    postCode: form.postCode,
    phone: form.phone,
    taxOffice: form.taxOffice,
    taxNumber: form.taxNumber,
    customerRelationId: null,
    vendorCurrencyTypeDeleted20251118071733: null,
  });

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp boş yeni kayda hazırlan.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.textId.trim() || !form.name.trim()) {
      showToast('error', 'Distribütör kodu ve adı zorunludur'); return;
    }
    // #768.1-3 / #771.1 Hane kuralları: alanlar zorunlu değil ama DOLDURULDUYSA tam olmalı.
    // Eskiden eksik girilse bile sessizce kaydediliyordu.
    if (!isExactDigits(form.postCode, 5)) { showToast('error', 'Posta kodu 5 haneli olmalıdır'); return; }
    if (!isExactDigits(form.identityNumber, 11)) { showToast('error', 'TC Kimlik No 11 haneli olmalıdır'); return; }
    if (!isExactDigits(form.taxNumber, 10)) { showToast('error', 'Vergi No 10 haneli olmalıdır'); return; }
    if (!isCompleteTrPhone(form.phone)) { showToast('error', 'Telefon numarası eksik — alan kodu dahil en az 10 hane girin veya alanı boş bırakın'); return; }
    setSaving(true);
    try {
      const payload = buildPayload();
      const res = await fetch(`${API_BASE}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(isNew ? payload : { id, ...payload }),
      });
      if (!res.ok) throw new Error(await readApiError(res, isNew ? 'Kayıt eklenemedi' : 'Kayıt güncellenemedi'));
      if (isNew) {
        if (andNew) {
          showToast('success', 'Distribütör eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Distribütör başarıyla eklendi');
        setTimeout(() => navigate('/vendors'), 400);
        return;
      }
      showToast('success', 'Distribütör başarıyla güncellendi');
      setTimeout(() => navigate('/vendors'), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut kaydın form bilgilerini taşıyarak yeni-kayıt ekranını aç (hiçbir şey kaydedilmez).
  const handleClone = () => {
    navigate('/vendors/new', { state: { clone: { ...form } } });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id }) });
      if (!res.ok) throw new Error(await readApiError(res, 'Kayıt silinemedi'));
      showToast('success', 'Distribütör başarıyla silindi');
      setTimeout(() => navigate('/vendors'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme işlemi sırasında hata oluştu');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const title = isNew ? 'Yeni Distribütör' : (form.name || 'Distribütör Detayı');
  const statusMeta = STATUS_META[form.status];
  const statusBadge = !isNew && statusMeta
    ? <StatusBadge tone={statusMeta.tone}>{statusMeta.label}</StatusBadge>
    : null;

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/vendors')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni Distribütör aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Distribütörler', to: '/vendors' }, { label: isNew ? 'Yeni Distribütör' : 'Distribütör Detayı', current: true }]}
      status={statusBadge}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'Vendor', entityId: id }}
      loading={loading}
    >
      <div className="dps-form">
        <Section icon="box" title="Distribütör Bilgileri" subtitle="Kod, ad, ilgili kişi ve ERP bilgileri">
          <Grid cols={2}>
            <FieldText form={form} set={set} tried={tried} k="textId" label="Distribütör Kodu" req mono />
            <FieldText form={form} set={set} tried={tried} k="name" label="Adı" req />
            {/* #768 İlgili Kişi eklendi */}
            <FieldText form={form} set={set} k="person" label="İlgili Kişi" />
            <FieldText form={form} set={set} k="erpId" label="ERP Kodu" />
          </Grid>
        </Section>

        {/* #768 Adres bölümü: İl/İlçe combobox (trGeo, Türkiye baz) + Adres + Posta Kodu */}
        <Section icon="mapPin" title="Adres" subtitle="İl, ilçe, adres ve posta kodu">
          <Grid cols={2}>
            {/* #768 Şehir → İl combobox (serbest metin yerine). İl değişince İlçe sıfırlanır.
                Eski serbest-metin değeri toOptions ile korunur ("(tanımsız)"). */}
            <FieldSelect form={form} set={set} k="city" label="İl"
              options={toOptions(getProvinces(GEO_COUNTRY), form.city)}
              onChange={(v) => setForm((c) => ({ ...c, city: v, district: '' }))} />
            {/* #768 İlçe: seçili İl'e bağlı kanonik ilçe listesi. İl seçili değilse pasif. */}
            <FieldSelect form={form} set={set} k="district" label="İlçe" disabled={!form.city}
              options={toOptions(getDistricts(GEO_COUNTRY, form.city), form.district)} />
            <FieldText form={form} set={set} k="address" label="Adres" span />
            {/* #768.1 Posta kodu: yalnız rakam, tam 5 hane. */}
            <FieldText form={form} set={set} k="postCode" label="Posta Kodu" mono
                       format={digitsOnly(5)} placeholder="34100" hint="5 haneli, yalnız rakam" />
          </Grid>
        </Section>

        {/* #768 İletişim & Vergi bölümü: Telefon (TR format) + TC + Vergi Dairesi/No */}
        <Section icon="phone" title="İletişim & Vergi" subtitle="Telefon, kimlik ve vergi bilgileri">
          <Grid cols={2}>
            {/* #771.1 formatTrPhoneInput: ayıraç silme tuzağına düşmez (alan kodu silinebilir). */}
            <FieldText form={form} set={set} k="phone" label="Telefon" format={formatTrPhoneInput}
                       hint="Alan kodu dahil 11 hane" />
            {/* #768.2 TC Kimlik No: yalnız rakam, en fazla 11 hane. */}
            <FieldText form={form} set={set} k="identityNumber" label="TC Kimlik No" mono
                       format={digitsOnly(11)} hint="11 haneli, yalnız rakam" />
            <FieldText form={form} set={set} k="taxOffice" label="Vergi Dairesi" />
            {/* #768.3 Vergi No: yalnız rakam, en fazla 10 hane. */}
            <FieldText form={form} set={set} k="taxNumber" label="Vergi No" mono
                       format={digitsOnly(10)} hint="10 haneli, yalnız rakam" />
          </Grid>
        </Section>

        <Section icon="layers" title="Gruplama & Bölge" subtitle="Distribütör grubu, ek grup ve bölge bilgileri">
          <Grid cols={2}>
            <FieldCustom label="Distribütör Grubu">
              <RelationPicker
                config={RELATION_CONFIGS.vendorGroup}
                rows={groups}
                value={form.groupId}
                selectedLabel={form.groupId ? getGroupLabel(form.groupId) : ''}
                onChange={(val) => set('groupId', val)}
                placeholder="Seçiniz"
              />
            </FieldCustom>
            <FieldCustom label="Ek Grup">
              <RelationPicker
                config={RELATION_CONFIGS.vendorSubGroup}
                rows={subGroups}
                value={form.subGroupId}
                selectedLabel={form.subGroupId ? getSubGroupLabel(form.subGroupId) : ''}
                onChange={(val) => set('subGroupId', val)}
                placeholder="Seçiniz"
              />
            </FieldCustom>
            <FieldText form={form} set={set} k="region1" label="Bölge 1" />
            <FieldText form={form} set={set} k="region2" label="Bölge 2" />
          </Grid>
        </Section>

        <Section icon="wallet" title="Durum & Para Birimi" subtitle="Kayıt durumu ve para birimi">
          <Grid cols={2}>
            <FieldSelect form={form} set={set} k="status" label="Durum" options={STATUS_OPTIONS} />
            {/* #768 Para Birimi → combobox (serbest metin yerine). Eski değer listede yoksa korunur. */}
            <FieldSelect form={form} set={set} k="vendorCurrency" label="Para Birimi" options={currencyOptions(form.vendorCurrency)} />
          </Grid>
        </Section>
      </div>

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{title}”</strong> distribütörsini silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default VendorDetailPage;
